import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    setState("loading");
    fetch(`/api/blog/${slug}`)
      .then((r) => { if (!r.ok) throw new Error("404"); return r.json(); })
      .then((d) => { setPost(d); setState("ok"); })
      .catch(() => setState("notfound"));
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="au-root">
      <style>{BODY_CSS}</style>
      <header style={S.header}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/blog" className="au-pill au-pill-glass au-pill-sm"><ArrowLeft /> All posts</Link>
      </header>

      <article className="au-container" style={S.container}>
        {state === "loading" && <div style={S.center}>Loading…</div>}

        {state === "notfound" && (
          <div style={S.center}>
            <h1 style={{ fontSize: 24, marginBottom: 12 }}>Post not found</h1>
            <Link to="/blog" className="au-pill au-pill-primary au-pill-sm">Back to blog</Link>
          </div>
        )}

        {state === "ok" && post && (
          <>
            <div className="au-eyebrow" style={{ marginBottom: 14 }}>{post.category || "Cybersecurity"}</div>
            <h1 style={S.h1}>{post.title}</h1>
            <div style={S.meta}>
              <span>{fmtDate(post.published_at)}</span>
              {(post.tags || []).length > 0 && <span style={{ color: "var(--au-border)" }}>·</span>}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(post.tags || []).map((t) => (
                  <span key={t} className="au-chip au-chip-cat au-cat-blue" style={{ fontSize: 10 }}>{t}</span>
                ))}
              </div>
            </div>

            {post.hero_image && (
              <img src={post.hero_image} alt="" style={S.hero} />
            )}

            <div className="blog-body" dangerouslySetInnerHTML={{ __html: post.body_html }} />

            <div style={{ marginTop: 36 }}>
              <Link to="/blog" className="au-pill au-pill-glass au-pill-sm"><ArrowLeft /> More articles</Link>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}

const S = {
  header: { maxWidth: 820, margin: "0 auto", padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 },
  container: { maxWidth: 820, padding: "24px 24px 60px" },
  center: { padding: 80, textAlign: "center", color: "var(--au-muted)" },
  h1: { fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)", margin: "0 0 16px", lineHeight: 1.2 },
  meta: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--au-muted)", marginBottom: 28, flexWrap: "wrap" },
  hero: { width: "100%", maxHeight: 420, objectFit: "cover", borderRadius: 14, marginBottom: 32, border: "1px solid var(--au-border)" },
};

// Typography for the injected article HTML.
const BODY_CSS = `
.blog-body { color: var(--au-text-2); font-size: 16px; line-height: 1.8; }
.blog-body h2 { color: var(--au-text); font-size: 1.5rem; margin: 2rem 0 .8rem; line-height: 1.3; }
.blog-body h3 { color: var(--au-text); font-size: 1.2rem; margin: 1.6rem 0 .6rem; }
.blog-body p { margin: 0 0 1.1rem; }
.blog-body ul, .blog-body ol { margin: 0 0 1.1rem 1.2rem; padding-left: 1rem; }
.blog-body li { margin: .4rem 0; }
.blog-body a { color: var(--au-accent-ink); text-decoration: underline; }
.blog-body strong { color: var(--au-text); }
.blog-body blockquote { margin: 1.4rem 0; padding: .6rem 1.2rem; border-left: 3px solid var(--au-accent); background: var(--au-glass); border-radius: 6px; color: var(--au-text); }
`;
